// src/views/admin/ADInscriptionNouvelEleve.jsx
//
// - Champ "Type d'élève" toujours affiché et OBLIGATOIRE dès qu'une classe
//   est choisie. Total des frais de scolarité calculé automatiquement.
// - Compte parent : creerUtilisateurAvecInvitation (lien d'activation par SMS).
// - Username pré-rempli "prenom.nom" (corrigé : point au lieu d'espace),
//   modifiable ; l'auto-génération s'arrête dès que l'utilisateur tape dedans.
// - NOUVEAU : le NOM DU PÈRE est pré-rempli avec le nom de l'élève (l'enfant
//   porte le nom du père) et suit ses modifications tant que l'utilisateur
//   n'a pas modifié lui-même le champ (nom_auto).
//   Si le père n'a QUE ce nom auto (rien d'autre saisi), aucune fiche père
//   n'est créée à l'enregistrement.
// - Écran de résultat : bouton "Payer la scolarité" vers la Caisse.
//
// Route attendue : /:slug/admin/eleves/nouveau
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminService } from "../../services/adminService";
import { Loader } from "../../components/Loader";
import { useToastHook } from "../../utils/useToast";

// ─── CONSTANTES ──────────────────────────────────────────────────────────────
const EMPTY_ELEVE = {
  matricule: "",
  nom: "",
  prenom: "",
  date_naissance: "",
  ne_vers: false,
  annee_naissance_approximative: "",
  lieu_naissance: "",
  genre: "M",
  adresse: "",
  nationalite: "",
  etat_sanitaire: "",
  situation_sociale: "",
  telephone_parent: "",
  photo: null,
};

const GENRES = [
  { value: "M", label: "Masculin" },
  { value: "F", label: "Féminin" },
];

const TAB_LABELS = {
  eleve: "Élève",
  tuteurs: "Tuteurs (Père / Mère)",
  inscription: "Inscription",
  abonnement: "Abonnement",
};
const TAB_ORDER = ["eleve", "tuteurs", "inscription", "abonnement"];

const LIEN_LABELS = { pere: "Père", mere: "Mère" };
const ARTICLE_LIEN_DE = { pere: "du père", mere: "de la mère" };
const ARTICLE_LIEN_OBJ = { pere: "le père", mere: "la mère" };

const EMPTY_TUTEUR = {
  nom: "",
  nom_auto: false, // true : le nom suit automatiquement celui de l'élève
  prenom: "",
  profession: "",
  adresse: "",
  telephone: "",
  compte_mode: "aucun",
  compte_username: "",
  compte_username_auto: true,
  compte_existant_search: "",
  compte_existant_selected: null,
};

// Seul le père reprend automatiquement le nom de l'enfant
const tuteursInitiaux = () => ({
  pere: { ...EMPTY_TUTEUR, nom_auto: true },
  mere: { ...EMPTY_TUTEUR },
});

const aujourdISO = () => new Date().toISOString().slice(0, 10);

const normaliserPourUsername = (str) =>
  (str || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

// Génère un nom d'utilisateur "prenom.nom"
const genererUsername = (t) => {
  const prenom = normaliserPourUsername(t.prenom);
  const nom = normaliserPourUsername(t.nom);
  if (prenom && nom) return `${prenom}.${nom}`; // ← CORRIGÉ (était un espace)
  return prenom || nom || "";
};

export default function ADInscriptionNouvelEleve({ slug }) {
  const navigate = useNavigate();
  const toast = useToastHook();
  const adminService = useAdminService();
  const {
    getClasses, getAnneesScolaires,
    createEleve, createInscription,
    createTuteur, lierCompteTuteur,
    getParents, creerUtilisateurAvecInvitation,
    getOffresPubliques, assignerOffreEleve,
    getNationalites, getEtatsSanitaires, getSituationsSociales,
    getProchainMatricule,
    getAffectationsFrais,
  } = adminService;
  const getTypesEleveFn = adminService.getTypesEleves;

  // ─── Chargement initial ──────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [classes, setClasses] = useState([]);
  const [annees, setAnnees] = useState([]);
  const [anneeActive, setAnneeActive] = useState(null);
  const [nationalites, setNationalites] = useState([]);
  const [etatsSanitaires, setEtatsSanitaires] = useState([]);
  const [situationsSociales, setSituationsSociales] = useState([]);
  const [typesEleve, setTypesEleve] = useState([]);

  const [tab, setTab] = useState("eleve");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  const [eleveInscritInfo, setEleveInscritInfo] = useState(null); // { eleveId, inscriptionId }

  // ─── Onglet Élève ─────────────────────────────────────────────────────────
  const [eleve, setEleve] = useState({ ...EMPTY_ELEVE });
  const [eleveErrors, setEleveErrors] = useState({});
  const [photoPreview, setPhotoPreview] = useState(null);

  // ─── Onglet Tuteurs ───────────────────────────────────────────────────────
  const [tuteurs, setTuteurs] = useState(tuteursInitiaux());
  const [tuteurRecherche, setTuteurRecherche] = useState({ pere: [], mere: [] });

  // ─── Onglet Inscription ───────────────────────────────────────────────────
  const [inscription, setInscription] = useState({
    classe: "", annee_scolaire: "",
    date_inscription: aujourdISO(),
    is_nouveau: true, is_redoublant: false, classe_redoublee: "", type_eleve: "",
  });

  const [totalScolarite, setTotalScolarite] = useState(null);
  const [totalScolariteLoading, setTotalScolariteLoading] = useState(false);
  const [affectationsClasse, setAffectationsClasse] = useState([]);

  // ─── Onglet Abonnement ─────────────────────────────────────────────────────
  const [offres, setOffres] = useState([]);
  const [offresLoading, setOffresLoading] = useState(false);
  const [offreChoisie, setOffreChoisie] = useState("");

  // ─── CHARGEMENT DES RÉFÉRENTIELS ──────────────────────────────────────────
  useEffect(() => {
    if (!slug) return;
    (async () => {
      setLoading(true);
      try {
        const [classesData, anneesData, nat, etats, situations] = await Promise.all([
          getClasses(slug, { all_years: 'true', show_archived: 'true' }),
          getAnneesScolaires(slug),
          getNationalites(slug),
          getEtatsSanitaires(slug, { actifs_seulement: "true" }),
          getSituationsSociales(slug, { actifs_seulement: "true" }),
        ]);
        setClasses(classesData || []);
        setAnnees(anneesData || []);
        const active = (anneesData || []).find(a => a.actif) || null;
        setAnneeActive(active);
        setInscription(prev => ({ ...prev, annee_scolaire: String(active?.id || "") }));
        setNationalites(nat || []);
        setEtatsSanitaires(etats || []);
        setSituationsSociales(situations || []);

        if (getTypesEleveFn) {
          try {
            const data = await getTypesEleveFn(slug);
            setTypesEleve(data?.results || data || []);
          } catch {
            setTypesEleve([]);
            toast.error("Impossible de charger les types d'élèves");
          }
        }

        try {
          const matriculeData = await getProchainMatricule(slug, active?.id);
          if (matriculeData?.matricule) {
            setEleve(prev => ({ ...prev, matricule: matriculeData.matricule }));
          }
        } catch {
          // silencieux
        }
      } catch {
        toast.error("Impossible de charger les données nécessaires à la création");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Recherche "compte existant" par tuteur
  useEffect(() => {
    ["pere", "mere"].forEach(lien => {
      const t = tuteurs[lien];
      if (t.compte_mode !== "existant") return;
      const timer = setTimeout(async () => {
        try {
          const data = await getParents(slug, t.compte_existant_search ? { search: t.compte_existant_search } : {});
          setTuteurRecherche(prev => ({ ...prev, [lien]: data.results || data || [] }));
        } catch {
          // silencieux
        }
      }, 300);
      return () => clearTimeout(timer);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tuteurs.pere.compte_existant_search, tuteurs.mere.compte_existant_search, tuteurs.pere.compte_mode, tuteurs.mere.compte_mode]);

  // Chargement des offres à l'ouverture de l'onglet Abonnement
  useEffect(() => {
    if (tab !== "abonnement" || offres.length > 0) return;
    setOffresLoading(true);
    getOffresPubliques()
      .then(d => setOffres(d.results || d || []))
      .catch(() => toast.error("Impossible de charger les offres d'abonnement"))
      .finally(() => setOffresLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // ─── TOTAL DES FRAIS DE SCOLARITÉ ─────────────────────────────────────────
  useEffect(() => {
    if (!inscription.classe || !inscription.type_eleve || !getAffectationsFrais) {
      setTotalScolarite(null);
      setAffectationsClasse([]);
      return;
    }
    let annule = false;
    (async () => {
      setTotalScolariteLoading(true);
      try {
        const affectations = await getAffectationsFrais(slug, { classe: inscription.classe });
        if (annule) return;
        const filtrees = (affectations || []).filter(a => String(a.type_eleve) === String(inscription.type_eleve));
        const total = filtrees.reduce((somme, a) => somme + Number(a.montant || 0), 0);
        setTotalScolarite(total);
        setAffectationsClasse(filtrees);
      } catch {
        if (!annule) { setTotalScolarite(null); setAffectationsClasse([]); }
      } finally {
        if (!annule) setTotalScolariteLoading(false);
      }
    })();
    return () => { annule = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inscription.classe, inscription.type_eleve]);

  // ─── HELPERS ───────────────────────────────────────────────────────────────
  const classesDisponibles = classes.filter(
    c => String(c.annee_scolaire) === String(inscription.annee_scolaire) && !c.archived
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString("fr-FR");
    } catch {
      return dateStr;
    }
  };

  const validerNaissance = (data, errors) => {
    if (data.ne_vers) {
      if (!data.annee_naissance_approximative) {
        errors.annee_naissance_approximative = "L'année de naissance est requise quand la date exacte est inconnue";
      }
    } else if (!data.date_naissance) {
      errors.date_naissance = "La date de naissance est requise (ou cochez « né vers »)";
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setEleve(prev => ({ ...prev, photo: null }));
      setPhotoPreview(null);
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast.error("Veuillez sélectionner une image valide");
      e.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 5MB");
      e.target.value = '';
      return;
    }
    setEleve(prev => ({ ...prev, photo: file }));
    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const buildInscriptionPayload = (eleveId) => {
    const data = {
      eleve: eleveId,
      classe: parseInt(inscription.classe),
      annee_scolaire: parseInt(inscription.annee_scolaire),
      date_inscription: inscription.date_inscription || aujourdISO(),
      is_nouveau: !!inscription.is_nouveau,
      is_redoublant: !!inscription.is_redoublant,
      type_eleve: parseInt(inscription.type_eleve),
    };
    if (inscription.is_redoublant && inscription.classe_redoublee) {
      data.classe_redoublee = parseInt(inscription.classe_redoublee);
    }
    return data;
  };

  // ─── NOM DE L'ÉLÈVE → NOM DU PÈRE (auto) ──────────────────────────────────
  const changerNomEleve = (valeur) => {
    setEleve(p => ({ ...p, nom: valeur }));
    setTuteurs(prev => {
      if (!prev.pere.nom_auto) return prev;
      const pere = { ...prev.pere, nom: valeur };
      if (pere.compte_mode === "nouveau" && pere.compte_username_auto) {
        pere.compte_username = genererUsername(pere);
      }
      return { ...prev, pere };
    });
  };

  // ─── TUTEURS ──────────────────────────────────────────────────────────────
  const setTuteurField = (lien, field, value) => {
    setTuteurs(prev => {
      const t = { ...prev[lien], [field]: value };
      if (field === "nom") t.nom_auto = false; // saisie manuelle → on arrête le pré-remplissage
      if (field === "compte_username") {
        t.compte_username_auto = false;
      } else if (
        (field === "nom" || field === "prenom") &&
        t.compte_mode === "nouveau" &&
        t.compte_username_auto
      ) {
        t.compte_username = genererUsername(t);
      }
      return { ...prev, [lien]: t };
    });
  };

  const setCompteMode = (lien, mode) => {
    setTuteurs(prev => {
      const autreLien = lien === "pere" ? "mere" : "pere";
      let t = { ...prev[lien], compte_mode: mode };
      if (mode === "nouveau" && t.compte_username_auto) {
        t.compte_username = genererUsername(t);
      }
      const next = { ...prev, [lien]: t };
      if (mode !== "aucun" && prev[autreLien].compte_mode !== "aucun") {
        next[autreLien] = { ...prev[autreLien], compte_mode: "aucun" };
      }
      return next;
    });
  };

  const getTuteurAvecCompte = () => {
    if (tuteurs.pere.compte_mode !== "aucun") return { lien: "pere", ...tuteurs.pere };
    if (tuteurs.mere.compte_mode !== "aucun") return { lien: "mere", ...tuteurs.mere };
    return null;
  };

  const getAbonnementActifCompteExistant = () => {
    const t = getTuteurAvecCompte();
    if (!t || t.compte_mode !== "existant" || !t.compte_existant_selected?.enfants?.length) return null;
    const enfantAvecAbo = t.compte_existant_selected.enfants.find(e => e.abonnement_actif);
    if (!enfantAvecAbo) return null;
    return { ...enfantAvecAbo.abonnement_actif, eleve_nom: `${enfantAvecAbo.prenom} ${enfantAvecAbo.nom}` };
  };

  // ─── VALIDATION ───────────────────────────────────────────────────────────
  const validerEtapeEleve = () => {
    const errors = {};
    if (!eleve.nom.trim()) errors.nom = "Le nom est requis";
    if (!eleve.prenom.trim()) errors.prenom = "Le prénom est requis";
    if (!eleve.matricule.trim()) errors.matricule = "Le matricule est requis";
    validerNaissance(eleve, errors);
    setEleveErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validerEtapeTuteurs = () => {
    for (const lien of ["pere", "mere"]) {
      const t = tuteurs[lien];
      if (t.compte_mode === "nouveau" && (!t.compte_username.trim() || !t.telephone.trim())) {
        toast.error(`${LIEN_LABELS[lien]} : nom d'utilisateur et téléphone requis pour créer un compte`);
        return false;
      }
      if (t.compte_mode === "existant" && !t.compte_existant_selected) {
        toast.error(`${LIEN_LABELS[lien]} : sélectionnez un compte existant, ou changez de mode`);
        return false;
      }
    }
    return true;
  };

  const validerEtapeInscription = () => {
    const { classe, annee_scolaire } = inscription;
    if ((classe && !annee_scolaire) || (!classe && annee_scolaire)) {
      toast.error("Sélectionnez à la fois l'année scolaire et la classe, ou laissez les deux vides pour inscrire plus tard");
      return false;
    }
    if (classe && !inscription.date_inscription) {
      toast.error("La date d'inscription est requise");
      return false;
    }
    if (classe && !inscription.type_eleve) {
      toast.error("Le type d'élève est requis (il détermine les frais de scolarité)");
      return false;
    }
    if (classe && inscription.is_redoublant && !inscription.classe_redoublee) {
      toast.error("Précisez la classe redoublée");
      return false;
    }
    return true;
  };

  const annuler = () => navigate(`/${slug}/admin/eleves`);

  // ─── SOUMISSION FINALE ────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validerEtapeEleve()) {
      setTab("eleve");
      toast.error("Veuillez corriger les erreurs du formulaire élève");
      return;
    }
    if (!validerEtapeTuteurs()) {
      setTab("tuteurs");
      return;
    }
    if (!validerEtapeInscription()) {
      setTab("inscription");
      return;
    }

    setBusy(true);
    let eleveCree = null;
    const recap = [];
    try {
      const tuteurAvecCompte = getTuteurAvecCompte();
      const telephoneParentPourEleve =
        tuteurAvecCompte?.telephone?.trim() ||
        tuteurs.pere.telephone.trim() ||
        tuteurs.mere.telephone.trim() ||
        "";

      const formData = new FormData();
      formData.append('matricule', eleve.matricule.trim().toUpperCase());
      formData.append('nom', eleve.nom.trim().toUpperCase());
      formData.append('prenom', eleve.prenom.trim());
      if (eleve.ne_vers) {
        formData.append('ne_vers', 'true');
        formData.append('annee_naissance_approximative', eleve.annee_naissance_approximative);
      } else {
        formData.append('ne_vers', 'false');
        formData.append('date_naissance', eleve.date_naissance);
      }
      formData.append('lieu_naissance', eleve.lieu_naissance?.trim() || "");
      formData.append('genre', eleve.genre || "M");
      formData.append('adresse', eleve.adresse?.trim() || "");
      if (eleve.nationalite) formData.append('nationalite', eleve.nationalite);
      if (eleve.etat_sanitaire) formData.append('etat_sanitaire', eleve.etat_sanitaire);
      if (eleve.situation_sociale) formData.append('situation_sociale', eleve.situation_sociale);
      formData.append('telephone_parent', telephoneParentPourEleve);
      if (eleve.photo instanceof File) formData.append('photo', eleve.photo);

      const eleveRes = await createEleve(slug, formData);
      const eleveObj = eleveRes.eleve || eleveRes;
      eleveCree = eleveObj;
      recap.push({
        label: "Élève", status: "success",
        message: `${eleveObj.prenom} ${eleveObj.nom} a été enregistré(e) avec succès (matricule ${eleveObj.matricule}).`,
      });

      // ── Tuteurs (Père / Mère) ──
      let finalParentId = null;
      for (const lien of ["pere", "mere"]) {
        const t = tuteurs[lien];
        // Un nom simplement repris de l'élève (nom_auto) ne suffit pas à
        // créer une fiche : il faut que l'utilisateur ait saisi autre chose.
        const rempli =
          (t.nom.trim() && !t.nom_auto) ||
          t.prenom.trim() ||
          t.telephone.trim() ||
          t.profession.trim() ||
          t.adresse.trim();
        if (!rempli) continue;

        try {
          const tuteurRes = await createTuteur(slug, {
            eleve: eleveObj.id,
            lien,
            nom: t.nom.trim(),
            prenom: t.prenom.trim(),
            profession: t.profession.trim(),
            adresse: t.adresse.trim(),
            telephone: t.telephone.trim(),
          });
          recap.push({
            label: LIEN_LABELS[lien], status: "success",
            message: `La fiche ${ARTICLE_LIEN_DE[lien]} a été enregistrée.`,
          });

          if (t.compte_mode === "nouveau") {
            const parentRes = await creerUtilisateurAvecInvitation(slug, {
              username: t.compte_username.trim(),
              first_name: t.prenom.trim(),
              last_name: t.nom.trim(),
              telephone1: t.telephone.trim(),
            });
            const parentId = parentRes.user?.id;
            await lierCompteTuteur(slug, tuteurRes.id, parentId);
            finalParentId = parentId;
            recap.push({
              label: LIEN_LABELS[lien], status: "success",
              message: `Un compte a été créé pour ${ARTICLE_LIEN_OBJ[lien]}. Un lien d'activation lui a été envoyé par SMS pour qu'il définisse son mot de passe.`,
            });
          } else if (t.compte_mode === "existant" && t.compte_existant_selected) {
            await lierCompteTuteur(slug, tuteurRes.id, t.compte_existant_selected.id);
            finalParentId = t.compte_existant_selected.id;
            recap.push({
              label: LIEN_LABELS[lien], status: "success",
              message: `Le compte existant de ${t.compte_existant_selected.first_name} ${t.compte_existant_selected.last_name} a été lié en tant que ${ARTICLE_LIEN_OBJ[lien]}.`,
            });
          }
        } catch (tErr) {
          const data = tErr.response?.data;
          const detail = data?.non_field_errors?.[0] || data?.error || data?.detail || "une erreur est survenue lors de l'enregistrement";
          recap.push({
            label: LIEN_LABELS[lien], status: "error",
            message: `La fiche ${ARTICLE_LIEN_DE[lien]} n'a pas pu être enregistrée (${detail}).`,
          });
        }
      }

      // ── Inscription (facultative) ──
      // L'API renvoie { success, inscription: {...}, message } → l'id est
      // dans inscription.id.
      let inscriptionId = null;
      if (inscription.classe && inscription.annee_scolaire) {
        const inscriptionRes = await createInscription(slug, buildInscriptionPayload(eleveObj.id));
        inscriptionId = inscriptionRes?.inscription?.id ?? inscriptionRes?.id ?? null;
        recap.push({
          label: "Inscription", status: "success",
          message: "L'élève a été inscrit(e) dans sa classe pour l'année scolaire sélectionnée.",
        });
      } else {
        recap.push({
          label: "Inscription", status: "warning",
          message: "Aucune inscription n'a été enregistrée pour l'instant — vous pourrez inscrire l'élève plus tard depuis sa fiche.",
        });
      }
      setEleveInscritInfo({ eleveId: eleveObj.id, inscriptionId });

      // ── Abonnement (facultatif, nécessite un parent lié) ──
      if (offreChoisie && finalParentId) {
        try {
          await assignerOffreEleve(slug, eleveObj.id, parseInt(offreChoisie));
          recap.push({ label: "Abonnement", status: "success", message: "L'abonnement a été attribué avec succès." });
        } catch (abError) {
          const data = abError.response?.data;
          let detail = "une erreur est survenue";
          if (data) {
            if (typeof data === "string") detail = data;
            else if (data.error) detail = data.error;
            else if (data.detail) detail = data.detail;
            else if (typeof data === "object") {
              const first = Object.entries(data)[0];
              if (first) detail = `${first[0]} : ${Array.isArray(first[1]) ? first[1].join(" ") : first[1]}`;
            }
          }
          recap.push({
            label: "Abonnement", status: "error",
            message: `L'abonnement n'a pas pu être attribué (${detail}).`,
          });
        }
      } else if (offreChoisie && !finalParentId) {
        recap.push({
          label: "Abonnement", status: "warning",
          message: "L'abonnement n'a pas été attribué : aucun tuteur avec compte utilisateur n'est lié à cet élève.",
        });
      }

      setResults(recap);
    } catch (error) {
      const msg =
        error.response?.data?.error ||
        error.response?.data?.detail ||
        (eleveCree
          ? "une erreur est survenue à une étape suivante — vérifiez sa fiche pour finaliser"
          : "impossible d'inscrire l'élève, vérifiez les informations saisies");
      recap.push({
        label: eleveCree ? "Étape suivante" : "Élève",
        status: "error",
        message: eleveCree
          ? `L'élève a bien été créé, mais ${msg}.`
          : `La création de l'élève a échoué : ${msg}.`,
      });
      setResults(recap);
    } finally {
      setBusy(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // ─── RENDU ──────────────────────────────────────────────────────────────────
  if (loading) return <Loader />;

  if (results) {
    const eleveEchoue = results.some(r => r.label === "Élève" && r.status === "error");
    const aDesErreurs = results.some(r => r.status === "error");
    const aDesAvertissements = results.some(r => r.status === "warning");

    let banniere;
    if (eleveEchoue) {
      banniere = { icon: "ti-circle-x", color: "#dc3545", titre: "La création de l'élève a échoué" };
    } else if (aDesErreurs) {
      banniere = { icon: "ti-alert-triangle", color: "#dc3545", titre: "L'élève a été créé, mais certaines étapes nécessitent votre attention" };
    } else if (aDesAvertissements) {
      banniere = { icon: "ti-info-circle", color: "#ffc107", titre: "Élève créé — quelques étapes restent à compléter" };
    } else {
      banniere = { icon: "ti-circle-check", color: "#28a745", titre: "Inscription enregistrée avec succès" };
    }

    return (
      <div className="page-inscription">
        <div className="page-header">
          <button className="ibtn" onClick={annuler}><i className="ti ti-arrow-left" /></button>
          <h2>Résultat de l'inscription</h2>
        </div>
        <div className="card" style={{ maxWidth: 640, margin: "0 auto", padding: 20 }}>

          <div style={{
            display: "flex", alignItems: "center", gap: 10, marginBottom: 16,
            padding: "12px 14px", borderRadius: 8, background: "var(--s2)",
          }}>
            <i className={`ti ${banniere.icon}`} style={{ color: banniere.color, fontSize: 22, flexShrink: 0 }} />
            <div style={{ fontWeight: 700, fontSize: 15 }}>{banniere.titre}</div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {results.map((r, i) => {
              const icon = r.status === "success" ? "ti-circle-check"
                : r.status === "warning" ? "ti-alert-triangle" : "ti-circle-x";
              const color = r.status === "success" ? "#28a745"
                : r.status === "warning" ? "#ffc107" : "#dc3545";
              return (
                <div key={i} style={{
                  display: "flex", alignItems: "flex-start", gap: 10,
                  padding: "10px 12px", background: "var(--s2)",
                  borderRadius: 6, borderLeft: `3px solid ${color}`,
                }}>
                  <i className={`ti ${icon}`} style={{ color, fontSize: 18, marginTop: 1, flexShrink: 0 }} />
                  <div>
                    <div style={{
                      fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                      letterSpacing: 0.4, color: "var(--h)", marginBottom: 2,
                    }}>
                      {r.label}
                    </div>
                    <div style={{ fontSize: 13, color: "var(--t)" }}>{r.message}</div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="factions" style={{ flexWrap: "wrap" }}>
            {eleveEchoue ? (
              <button className="bsec" onClick={() => setResults(null)}>
                <i className="ti ti-arrow-back-up" /> Corriger et réessayer
              </button>
            ) : (
              <>
                {eleveInscritInfo?.inscriptionId && (
                  <button
                    className="bprim"
                    onClick={() => navigate(`/${slug}/admin/caisse?tab=situation&inscription=${eleveInscritInfo.inscriptionId}`)}
                  >
                    <i className="ti ti-cash" /> Payer la scolarité
                  </button>
                )}
                <button
                  className="bsec"
                  onClick={() => {
                    setResults(null);
                    setEleveInscritInfo(null);
                    setEleve({ ...EMPTY_ELEVE });
                    setEleveErrors({});
                    setPhotoPreview(null);
                    setTuteurs(tuteursInitiaux());
                    setInscription({
                      classe: "", annee_scolaire: String(anneeActive?.id || ""),
                      date_inscription: aujourdISO(),
                      is_nouveau: true, is_redoublant: false, classe_redoublee: "", type_eleve: "",
                    });
                    setOffreChoisie("");
                    setTab("eleve");
                    getProchainMatricule(slug, anneeActive?.id)
                      .then(d => d?.matricule && setEleve(p => ({ ...p, matricule: d.matricule })))
                      .catch(() => {});
                  }}
                >
                  <i className="ti ti-user-plus" /> Inscrire un autre élève
                </button>
              </>
            )}
            <button className="bsec" onClick={annuler}>
              <i className="ti ti-check" /> Retour à la liste des élèves
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-inscription">
      <div className="page-header">
        <button className="ibtn" onClick={annuler} title="Retour"><i className="ti ti-arrow-left" /></button>
        <h2>Fiche d'inscription complète</h2>
      </div>

      <div className="tabs-nav">
        {TAB_ORDER.map(key => (
          <button
            key={key}
            type="button"
            className={`tab-item ${tab === key ? "active" : ""}`}
            onClick={() => setTab(key)}
          >
            {TAB_LABELS[key]}
          </button>
        ))}
      </div>

      <div className="card" style={{ maxWidth: 780, margin: "0 auto", padding: 24 }}>
        {tab === "eleve" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            <div className="fgrid">
              <div className="fg">
                <label>Matricule <span style={{ color: "var(--rd)" }}>*</span></label>
                <input
                  type="text" placeholder="Ex: ELEVE-001"
                  value={eleve.matricule}
                  onChange={e => setEleve(p => ({ ...p, matricule: e.target.value.toUpperCase() }))}
                  style={eleveErrors.matricule ? { borderColor: "var(--rd)" } : {}}
                />
                {eleveErrors.matricule && <span style={{ color: "var(--rd)", fontSize: 12 }}>{eleveErrors.matricule}</span>}
                <span style={{ fontSize: 11, color: "var(--h)" }}>Prérempli automatiquement, modifiable.</span>
              </div>
              <div className="fg">
                <label>Genre</label>
                <select value={eleve.genre} onChange={e => setEleve(p => ({ ...p, genre: e.target.value }))}>
                  {GENRES.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                </select>
              </div>
            </div>
            <div className="fgrid">
              <div className="fg">
                <label>Nom <span style={{ color: "var(--rd)" }}>*</span></label>
                <input
                  type="text" placeholder="Ex: OUEDRAOGO"
                  value={eleve.nom}
                  onChange={e => changerNomEleve(e.target.value.toUpperCase())}
                  style={eleveErrors.nom ? { borderColor: "var(--rd)" } : {}}
                />
                {eleveErrors.nom && <span style={{ color: "var(--rd)", fontSize: 12 }}>{eleveErrors.nom}</span>}
              </div>
              <div className="fg">
                <label>Prénom <span style={{ color: "var(--rd)" }}>*</span></label>
                <input
                  type="text" placeholder="Ex: Fatima"
                  value={eleve.prenom}
                  onChange={e => setEleve(p => ({ ...p, prenom: e.target.value }))}
                  style={eleveErrors.prenom ? { borderColor: "var(--rd)" } : {}}
                />
                {eleveErrors.prenom && <span style={{ color: "var(--rd)", fontSize: 12 }}>{eleveErrors.prenom}</span>}
              </div>
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={eleve.ne_vers}
                onChange={e => setEleve(p => ({ ...p, ne_vers: e.target.checked }))}
              />
              Date de naissance exacte inconnue (« né(e) vers »)
            </label>

            <div className="fgrid">
              {eleve.ne_vers ? (
                <div className="fg">
                  <label>Année de naissance approximative <span style={{ color: "var(--rd)" }}>*</span></label>
                  <input
                    type="number" placeholder="Ex: 2015"
                    value={eleve.annee_naissance_approximative}
                    onChange={e => setEleve(p => ({ ...p, annee_naissance_approximative: e.target.value }))}
                    style={eleveErrors.annee_naissance_approximative ? { borderColor: "var(--rd)" } : {}}
                  />
                  {eleveErrors.annee_naissance_approximative && (
                    <span style={{ color: "var(--rd)", fontSize: 12 }}>{eleveErrors.annee_naissance_approximative}</span>
                  )}
                </div>
              ) : (
                <div className="fg">
                  <label>Date de naissance <span style={{ color: "var(--rd)" }}>*</span></label>
                  <input
                    type="date"
                    value={eleve.date_naissance}
                    onChange={e => setEleve(p => ({ ...p, date_naissance: e.target.value }))}
                    style={eleveErrors.date_naissance ? { borderColor: "var(--rd)" } : {}}
                  />
                  {eleveErrors.date_naissance && <span style={{ color: "var(--rd)", fontSize: 12 }}>{eleveErrors.date_naissance}</span>}
                </div>
              )}
              <div className="fg">
                <label>Lieu de naissance</label>
                <input
                  type="text" placeholder="Ex: Ouagadougou"
                  value={eleve.lieu_naissance}
                  onChange={e => setEleve(p => ({ ...p, lieu_naissance: e.target.value }))}
                />
              </div>
            </div>

            <div className="fg">
              <label>Adresse</label>
              <input
                type="text" placeholder="Ex: Secteur 12, Ouagadougou"
                value={eleve.adresse}
                onChange={e => setEleve(p => ({ ...p, adresse: e.target.value }))}
              />
            </div>

            <div className="fgrid">
              <div className="fg">
                <label>Nationalité</label>
                <select value={eleve.nationalite} onChange={e => setEleve(p => ({ ...p, nationalite: e.target.value }))}>
                  <option value="">— Non renseignée —</option>
                  {nationalites.map(n => <option key={n.id} value={n.id}>{n.nom}</option>)}
                </select>
              </div>
              <div className="fg">
                <label>État sanitaire</label>
                <select value={eleve.etat_sanitaire} onChange={e => setEleve(p => ({ ...p, etat_sanitaire: e.target.value }))}>
                  <option value="">— Non renseigné —</option>
                  {etatsSanitaires.map(e => <option key={e.id} value={e.id}>{e.libelle}</option>)}
                </select>
              </div>
            </div>
            <div className="fg">
              <label>Situation sociale</label>
              <select value={eleve.situation_sociale} onChange={e => setEleve(p => ({ ...p, situation_sociale: e.target.value }))}>
                <option value="">— Non renseignée —</option>
                {situationsSociales.map(s => <option key={s.id} value={s.id}>{s.libelle}</option>)}
              </select>
            </div>

            <div className="fg">
              <label>Photo</label>
              <input type="file" accept="image/*" onChange={handlePhotoChange} style={{ padding: "6px" }} />
              {photoPreview && (
                <img src={photoPreview} alt="" style={{ width: 70, height: 70, borderRadius: "50%", objectFit: "cover", marginTop: 8 }} />
              )}
            </div>
          </div>
        )}

        {tab === "tuteurs" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <div style={{ fontSize: 12, color: "var(--h)" }}>
              Renseignez le père et/ou la mère. Un seul des deux peut avoir un compte utilisateur
              (accès parent à l'application) — cochez « Créer un compte » ou « Lier un compte existant »
              sur celui qui doit l'avoir.
            </div>

            {["pere", "mere"].map(lien => {
              const t = tuteurs[lien];
              return (
                <div key={lien} style={{ border: "1px solid var(--b)", borderRadius: 8, padding: 14 }}>
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>{LIEN_LABELS[lien]}</div>
                  <div className="fgrid">
                    <div className="fg">
                      <label>Nom</label>
                      <input value={t.nom} onChange={e => setTuteurField(lien, 'nom', e.target.value.toUpperCase())} />
                      {lien === "pere" && t.nom_auto && t.nom && (
                        <span style={{ fontSize: 11, color: "var(--h)" }}>Repris du nom de l'élève — modifiable.</span>
                      )}
                    </div>
                    <div className="fg">
                      <label>Prénom</label>
                      <input value={t.prenom} onChange={e => setTuteurField(lien, 'prenom', e.target.value)} />
                    </div>
                  </div>
                  <div className="fgrid">
                    <div className="fg">
                      <label>Profession</label>
                      <input value={t.profession} onChange={e => setTuteurField(lien, 'profession', e.target.value)} />
                    </div>
                    <div className="fg">
                      <label>Téléphone</label>
                      <input value={t.telephone} onChange={e => setTuteurField(lien, 'telephone', e.target.value)} />
                    </div>
                  </div>
                  <div className="fg">
                    <label>Adresse</label>
                    <input value={t.adresse} onChange={e => setTuteurField(lien, 'adresse', e.target.value)} />
                  </div>

                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed var(--b)" }}>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {[["aucun", "Sans compte"], ["nouveau", "Créer un compte"], ["existant", "Lier un compte existant"]].map(([val, label]) => (
                        <button
                          key={val}
                          type="button"
                          className={t.compte_mode === val ? "bprim" : "bsec"}
                          style={{ fontSize: 12, padding: "5px 10px" }}
                          onClick={() => setCompteMode(lien, val)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>

                    {t.compte_mode === "nouveau" && (
                      <div className="fg" style={{ marginTop: 8 }}>
                        <label>Nom d'utilisateur <span style={{ color: "var(--rd)" }}>*</span></label>
                        <input
                          value={t.compte_username}
                          onChange={e => setTuteurField(lien, 'compte_username', e.target.value)}
                        />
                        <span style={{ fontSize: 11, color: "var(--h)" }}>
                          Pré-rempli automatiquement à partir du nom et prénom — modifiable si besoin.
                          Un lien d'activation sera envoyé par SMS pour que {ARTICLE_LIEN_OBJ[lien]} définisse son mot de passe.
                        </span>
                      </div>
                    )}

                    {t.compte_mode === "existant" && (
                      <div style={{ marginTop: 8 }}>
                        <input
                          placeholder="Rechercher un parent (nom, téléphone)..."
                          value={t.compte_existant_search}
                          onChange={e => {
                            setTuteurField(lien, 'compte_existant_search', e.target.value);
                            setTuteurField(lien, 'compte_existant_selected', null);
                          }}
                          style={{ width: "100%" }}
                        />
                        <div style={{ maxHeight: 180, overflowY: "auto", marginTop: 8 }}>
                          {(tuteurRecherche[lien] || []).map(p => (
                            <div
                              key={p.id}
                              onClick={() => setTuteurField(lien, 'compte_existant_selected', p)}
                              style={{
                                padding: "8px 10px", cursor: "pointer", borderRadius: 6, fontSize: 13,
                                background: t.compte_existant_selected?.id === p.id ? "var(--bl-bg)" : "var(--s2)", marginBottom: 4,
                              }}
                            >
                              <strong>{p.first_name} {p.last_name}</strong>
                              {" — "}
                              {[p.telephone1, p.telephone2].filter(Boolean).join(" / ") || "—"}
                            </div>
                          ))}
                          {(tuteurRecherche[lien] || []).length === 0 && (
                            <div style={{ fontSize: 13, color: "var(--h)" }}>Aucun résultat</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {tab === "inscription" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            <div style={{ fontSize: 14, color: "var(--m)" }}>
              Élève : <strong style={{ color: "var(--t)" }}>{eleve.prenom} {eleve.nom}</strong> ({eleve.matricule || "matricule à définir"})
            </div>
            <div style={{ fontSize: 12, color: "var(--h)" }}>
              Cette étape est facultative — laissez les deux champs vides pour inscrire l'élève plus tard depuis sa fiche.
            </div>
            <div className="fgrid">
              <div className="fg">
                <label>Année scolaire</label>
                <select
                  value={inscription.annee_scolaire}
                  onChange={e => setInscription(p => ({ ...p, annee_scolaire: e.target.value, classe: "" }))}
                >
                  <option value="">— Sélectionner —</option>
                  {annees.filter(a => !a.archived).map(a => (
                    <option key={a.id} value={a.id}>{a.nom} {a.actif ? "✓" : ""}</option>
                  ))}
                </select>
              </div>
              <div className="fg">
                <label>Classe</label>
                <select
                  value={inscription.classe}
                  onChange={e => setInscription(p => ({ ...p, classe: e.target.value }))}
                  disabled={!inscription.annee_scolaire}
                >
                  <option value="">— Sélectionner —</option>
                  {classesDisponibles.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                </select>
              </div>
            </div>

            <div className="fg">
              <label>Date d'inscription <span style={{ color: "var(--rd)" }}>*</span></label>
              <input
                type="date"
                value={inscription.date_inscription}
                onChange={e => setInscription(p => ({ ...p, date_inscription: e.target.value }))}
              />
              <span style={{ fontSize: 11, color: "var(--h)" }}>Préremplie à aujourd'hui, modifiable si besoin.</span>
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={inscription.is_nouveau}
                onChange={e => setInscription(p => ({ ...p, is_nouveau: e.target.checked }))}
              />
              Nouvel élève dans l'établissement (1ère inscription)
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={inscription.is_redoublant}
                onChange={e => setInscription(p => ({ ...p, is_redoublant: e.target.checked, classe_redoublee: "" }))}
              />
              Redoublant
            </label>
            {inscription.is_redoublant && (
              <div className="fg">
                <label>Classe redoublée <span style={{ color: "var(--rd)" }}>*</span></label>
                <select
                  value={inscription.classe_redoublee}
                  onChange={e => setInscription(p => ({ ...p, classe_redoublee: e.target.value }))}
                >
                  <option value="">— Sélectionner —</option>
                  {classes.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                </select>
              </div>
            )}

            <div className="fg">
              <label>Type d'élève <span style={{ color: "var(--rd)" }}>*</span></label>
              <select
                value={inscription.type_eleve}
                onChange={e => setInscription(p => ({ ...p, type_eleve: e.target.value }))}
                disabled={typesEleve.length === 0}
              >
                <option value="">— Sélectionner —</option>
                {typesEleve.map(t => <option key={t.id} value={t.id}>{t.libelle}</option>)}
              </select>
              {typesEleve.length === 0 && (
                <span style={{ fontSize: 11, color: "var(--rd)" }}>
                  Aucun type d'élève configuré — allez dans Caisse → Configuration → Types d'élèves
                </span>
              )}
            </div>

            {inscription.classe && inscription.type_eleve && (
              <div style={{
                padding: "10px 12px", borderRadius: 8, background: "var(--bl-bg)",
              }}>
                <div style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  fontWeight: 700, fontSize: 14,
                }}>
                  <span>Total frais de scolarité à payer</span>
                  <span>
                    {totalScolariteLoading
                      ? "Calcul..."
                      : totalScolarite !== null ? `${totalScolarite.toLocaleString()} FCFA` : "—"}
                  </span>
                </div>
                {!totalScolariteLoading && affectationsClasse.length === 0 && (
                  <div style={{ fontSize: 12, color: "var(--rd)", marginTop: 4 }}>
                    Aucun frais affecté pour cette classe et ce type d'élève — configurez-les dans
                    Caisse → Configuration → Affectations.
                  </div>
                )}
                {!totalScolariteLoading && affectationsClasse.length > 0 && (
                  <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 2 }}>
                    {affectationsClasse.map(a => (
                      <div key={a.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--m)" }}>
                        <span>{a.tranche_libelle || `Tranche #${a.tranche}`}</span>
                        <span>{Number(a.montant).toLocaleString()} FCFA</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {tab === "abonnement" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            {!getTuteurAvecCompte() && (
              <div style={{ fontSize: 13, color: "var(--rd)" }}>
                Aucun tuteur avec compte : l'abonnement ne pourra pas être attribué maintenant.
              </div>
            )}
            {getAbonnementActifCompteExistant() && (
              <div style={{ fontSize: 13, background: "var(--bl-bg)", padding: "8px 10px", borderRadius: 6 }}>
                <i className="ti ti-info-circle" /> Ce compte a déjà un abonnement actif{" "}
                <strong>{getAbonnementActifCompteExistant().offre_nom}</strong> pour{" "}
                {getAbonnementActifCompteExistant().eleve_nom} (jusqu'au{" "}
                {formatDate(getAbonnementActifCompteExistant().date_fin)}).
              </div>
            )}
            <div className="fg">
              <label>Offre d'abonnement (optionnel)</label>
              <select
                value={offreChoisie}
                onChange={e => setOffreChoisie(e.target.value)}
                disabled={offresLoading || !getTuteurAvecCompte()}
              >
                <option value="">— Aucune —</option>
                {offres.map(o => (
                  <option key={o.id} value={o.id}>{o.nom} — {o.prix} FCFA / {o.duree_jours}j</option>
                ))}
              </select>
              {offresLoading && <span style={{ fontSize: 12, color: "var(--h)" }}>Chargement des offres...</span>}
            </div>
          </div>
        )}

        <div className="factions" style={{ justifyContent: "space-between" }}>
          <button className="bsec" onClick={annuler} disabled={busy}>Annuler</button>
          <button className="bprim" onClick={handleSubmit} disabled={busy}>
            {busy
              ? <><i className="ti ti-loader" style={{ animation: "spin 1s linear infinite" }} /> Création en cours...</>
              : <><i className="ti ti-check" /> Inscrire l'élève</>}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        .page-inscription { padding: 8px 4px 40px; }
        .page-header { display: flex; align-items: center; gap: 12px; margin-bottom: 18px; }
        .page-header h2 { font-size: 18px; margin: 0; }
        .ibtn {
          width: 36px; height: 36px; border-radius: 8px; border: 1px solid var(--b);
          background: var(--s2); cursor: pointer; display: flex; align-items: center; justify-content: center;
        }
        .tabs-nav {
          display: flex; gap: 6px; max-width: 780px; margin: 0 auto 16px; flex-wrap: wrap;
        }
        .tab-item {
          flex: 1; min-width: 140px; text-align: center; padding: 9px 8px; border-radius: 6px;
          font-size: 12px; font-weight: 600; background: var(--s2); color: var(--h);
          border: none; cursor: pointer; transition: all .15s;
        }
        .tab-item:hover { background: var(--b); }
        .tab-item.active { background: var(--pr); color: #fff; }
      `}</style>
    </div>
  );
}